<template>
  <BaseModal @close="$emit('close')" cardWidth="520px" noPadding noToolbar noExit>
    <template #default>
      <!-- Content -->
      <div class="content-grid border-b border-border-normal">
        <!-- Left Side -->
        <div class="w-full h-full flex flex-col p-5">
          <BaseText type="label" size="sm" class="text-text-muted">
            Create a New Lens Brand
          </BaseText>
          <BaseText type="body" size="sm" class="text-text-normal mt-1">
            You are about to create a new Lens Brand. A Lens brand can hold multiple ad accounts (from Meta, TikTok, etc.) under one dashboard.
          </BaseText>
        </div>
        <!-- Preview -->
        <div class="relative w-full h-full overflow-hidden pointer-events-none">
          <LensBrandModalPreview />
        </div>
      </div>
      <!-- Actions Footer -->
      <div class="w-full flex flex-col p-2">
        <div class="relative w-full flex items-center justify-between p-3">
          <!-- Cancel -->
          <button class="px-2.5 py-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-25"
          @click="$emit('close')">
            <BaseText type="label" size="sm" class="text-text-muted">
              Cancel
            </BaseText>
          </button>
          <!-- Submit -->
          <button class="submit-btn flex items-center gap-1 pl-2.5 pr-1.5 py-1.5 rounded-md"
          :class="maxLensUsed ? 'disabled' : 'enabled'" @click="handleSubmit">
            <BaseText type="label" size="sm" class="transition-colors duration-100"
            :class="maxLensUsed ? 'text-text-disabled' : 'text-white'">
              Next
            </BaseText>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"
            class="transition-colors duration-100" :class="maxLensUsed ? 'text-text-disabled' : 'text-white'">
              <path d="M8.33301 13.3332L11.0771 10.5891C11.4025 10.2637 11.4025 9.73602 11.0771 9.41058L8.33301 6.6665" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <!-- Credit Usage -->
          <div ref="creditUsage" class="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-max flex items-center gap-1 pl-1 py-1 pr-3 rounded-full border border-border-normal mt-auto cursor-default">
            <LensCreditIcon />
            <BaseText type="label" size="sm" class="text-text-muted">
              {{ getSidebarLenses?.length }}/{{ getLensLimit }} Lens Brands
            </BaseText>
          </div>
        </div>
        <!-- Max Lens Used CTA -->
        <div v-if="maxLensUsed" class="w-full flex flex-col gap-2 p-2 rounded-xl bg-neutral-25 mt-2">
          <!-- Copy -->
          <div class="w-full flex gap-2 p-1">
            <InfoIcon class="text-icon-normal flex-shrink-0" />
            <div class="flex-grow flex flex-col gap-1">
              <BaseText type="label" size="sm" class="text-text-muted">
                You have reached your Lens Brand limit
              </BaseText>
              <BaseText type="body" size="sm" class="text-text-normal">
                Add more Lens Brands to your plan to continue.
              </BaseText>
            </div>
          </div>
          <!-- Actions -->
          <div class="w-full grid grid-cols-2 gap-2">
            <button class="secondary-btn" @click="openDemoBook">
              <BaseText type="label" size="sm" class="text-text-muted">
                Book a Demo
              </BaseText>
            </button>
            <button class="secondary-btn" @click="openGetMore">
              <BaseText type="label" size="sm" class="text-text-muted">
                Get More
              </BaseText>
            </button>
          </div>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import LensBrandModalPreview from './LensBrandModalPreview.vue'
import LensCreditIcon from '../../../globals/Icons/LensIcons/LensCreditIcon.vue'
import ChevronIcon from '../../../globals/Icons/ChevronIcon.vue'
import InfoIcon from '../../../globals/Icons/InfoSimpleIcon.vue'

export default {
  name: 'LensCreationModal',
  components: {
    LensBrandModalPreview,
    LensCreditIcon,
    ChevronIcon,
    InfoIcon
  },
  computed: {
    ...mapGetters('LensModule', ['getSidebarLenses']),
    ...mapGetters('AuthModule', ['getLensLimit']),
    maxLensUsed () {
      const totalLenses = this.getSidebarLenses?.length ?? 0
      return totalLenses >= this.getLensLimit
    }
  },
  methods: {
    ...mapMutations('MiscModule', ['SET_SETTINGS_DRAWER_VIEW_MODE']),
    handleSubmit () {
      if (this.maxLensUsed) {
        this.animateShake()
      } else {
        this.$emit('proceed')
      }
    },
    openDemoBook () {
      window.open('https://www.foreplay.co/book-demo', '_blank')
    },
    openGetMore () {
      this.SET_SETTINGS_DRAWER_VIEW_MODE('plan')
      this.$emit('close')
    },
    animateShake () {
      const creditUsage = this.$refs.creditUsage
      if (creditUsage.classList.contains('anim-shake')) return
      creditUsage.classList.add('anim-shake')
      creditUsage.addEventListener('animationend', () => {
        if (creditUsage) creditUsage.classList.remove('anim-shake')
      })
    }
  }
}
</script>

<style scoped>
.content-grid {
  width: 100%;
  height: 198px;
  display: grid;
  grid-template-columns: 1fr 260px;
}

@property --submit-btn-opacity-1 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
.submit-btn {
  --submit-color: #121212;
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--submit-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--submit-btn-opacity-2)) 100%), var(--submit-color);
  color: white;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px var(--submit-color);
  transition: box-shadow 100ms ease-in-out,
    color 100ms ease-in-out,
    background-color 100ms ease-in-out,
    --submit-btn-opacity-1 100ms ease-in-out,
    --submit-btn-opacity-2 100ms ease-in-out,
    --submit-color 100ms ease-in-out;
}
.submit-btn.enabled:hover {
  --submit-btn-opacity-1: 0.24;
  --submit-btn-opacity-2: 0;
}
.submit-btn.disabled {
  color: #A4ABB8;
  box-shadow: none;
  --submit-btn-opacity-1: 0;
  --submit-btn-opacity-2: 0;
  --submit-color: #F6F8FA;
}

.secondary-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border-radius: 6px;
  background-color: white;
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.12), 0 0 0 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out;
}
.secondary-btn:hover {
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.16), 0 0 0 1px rgba(0, 56, 108, 0.12);
}

.anim-shake {
  animation: shake 350ms ease-in-out;
}
@keyframes shake {
  0% { transform: translate(calc(-50%), -50%); }
  25% { transform: translate(calc(-50% + 8px), -50%); }
  50% { transform: translate(calc(-50% - 6px), -50%); }
  75% { transform: translate(calc(-50% + 2px), -50%); }
  100% { transform: translate(calc(-50%), -50%); }
}
</style>